import React, { useRef, useCallback, useState, useMemo, forwardRef } from 'react';
import { Arrow } from './arrow';
import { Subtitle2 } from '../typography';
import { TooltipWrapper } from './styled';
import useControlled from '../../hooks/useControlled';
import useForkRef from '../../hooks/useForkRef';
import { composeMouseEventHandler, composeFocusEventHandler } from './utils';
/**
 * @typedef {'focus' | 'hover'} TriggerEvent
 * @typedef {Object} TriggerEventList
 * @property {true} [focus]
 * @property {true} [hover]
 *
 * @typedef {Object} InteractiveListeners
 * @property {(event: React.MouseEvent<HTMLElement>) => void} [onMouseOver]
 * @property {(event: React.MouseEvent<HTMLElement>) => void} [onMouseLeave]
 * @property {(event: React.FocusEvent<HTMLElement>) => void} [onFocus]
 * @property {(event: React.FocusEvent<HTMLElement>) => void} [onBlur]
 *
 * @typedef {'top' | 'bottom' | 'left' | 'right'} TooltipPositionT
 *
 * @typedef {Object} ITooltipProps
 * @extends {Omit<IPopoverProps, 'positions' | 'anchorEl'>}
 * @property {ReactNode} content - The content of the tooltip.
 * @property {PopoverContentPosition | PopoverContentPosition[]} [position] - The position of the tooltip.
 * @property {ReactElement} children - The element that triggers the tooltip.
 * @property {boolean} [isWhite] - If true, the tooltip will have a white background.
 * @property {TriggerEvent[]} [triggerBy] - Events that trigger the tooltip.
 * @property {boolean} [isOpen] - Controls whether the tooltip is open.
 * @property {(event: ChangeEvent<any>) => void} [onOpen] - Callback fired when the tooltip is opened.
 * @property {(event: ChangeEvent<any>) => void} [onClose] - Callback fired when the tooltip is closed.
 * @property {string} [className] - Additional class names to style the tooltip.
 * @property {boolean} [disabled] - If true, the tooltip will be disabled.
 * @property {boolean} [withArrow] - If true, the tooltip will have an arrow pointing to the trigger element.
 */
const TRIGGER_BY = ['focus', 'hover'];
const TOOLTIP_POSITION_ORDER = ['top', 'bottom', 'left', 'right'];
let hystersisOpen = false;
let hystersisTimer = null;
const ENTER_DELAY_MS = 100;
const LEAVE_DELAY_MS = 0;
export const Tooltip = forwardRef(({ content, children, onOpen, onClose, position = TOOLTIP_POSITION_ORDER, isOpen: isOpenProp, isWhite, triggerBy = TRIGGER_BY, disabled = false, withArrow = true, ...props }, ref) => {
    const positions = typeof position === 'string' ? [position] : position;
    const [anchorEl, setAnchorEl] = useState(null);
    const [arrowEl, setArrowEl] = useState(null);
    const closeTimer = useRef();
    const enterTimer = useRef();
    const leaveTimer = useRef();
    const [isOpen, setOpen] = useControlled({
        controlled: isOpenProp,
        default: false
    });
    const handleUseRef = useForkRef(setAnchorEl, ref);
    /* @ts-ignore */
    const handleRef = useForkRef(children === null || children === void 0 ? void 0 : children.ref, handleUseRef);
    const wrappedContent = useMemo(() => {
        if (typeof content === 'string') {
            return React.createElement(Subtitle2, { as: "p" }, content);
        }
        else {
            return content;
        }
    }, [content]);
    const modifiers = useMemo(() => {
        return [
            {
                name: 'arrow',
                enabled: Boolean(arrowEl),
                options: {
                    element: arrowEl,
                    padding: 6
                }
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 6]
                }
            }
        ];
    }, [arrowEl]);
    const triggerList = triggerBy.reduce((acc, eventName) => {
        acc[eventName] = true;
        return acc;
    }, {});
    const handleOpen = (event) => {
        if (disabled)
            return;
        if (hystersisTimer) {
            clearTimeout(hystersisTimer);
        }
        hystersisOpen = true;
        setOpen(true);
        if (onOpen && !isOpen) {
            onOpen(event);
        }
    };
    const handleClose = useCallback((event) => {
        if (hystersisTimer) {
            clearTimeout(hystersisTimer);
        }
        hystersisTimer = setTimeout(() => {
            hystersisOpen = false;
        }, 800);
        setOpen(false);
        if (onClose && isOpen) {
            onClose(event);
        }
        clearTimeout(closeTimer.current);
    }, [isOpen, hystersisTimer, hystersisOpen, onClose]);
    const handleEnter = (event) => {
        clearTimeout(enterTimer.current);
        clearTimeout(leaveTimer.current);
        enterTimer.current = setTimeout(() => {
            handleOpen(event);
        }, hystersisOpen ? 0 : ENTER_DELAY_MS);
    };
    const handleLeave = (event) => {
        clearTimeout(enterTimer.current);
        clearTimeout(leaveTimer.current);
        leaveTimer.current = setTimeout(() => {
            handleClose(event);
        }, LEAVE_DELAY_MS);
    };
    const handleMouseOver = handleEnter;
    const handleMouseLeave = handleLeave;
    const handleFocus = (event) => {
        handleEnter(event);
    };
    const handleBlur = (event) => {
        handleLeave(event);
    };
    const childrenProps = {
        ...children.props,
        ref: handleRef
    };
    const interactiveListeners = {};
    if (triggerList.hover) {
        childrenProps.onMouseOver = composeMouseEventHandler(handleMouseOver, childrenProps.onMouseOver);
        childrenProps.onMouseLeave = composeMouseEventHandler(handleMouseLeave, childrenProps.onMouseLeave);
        interactiveListeners.onMouseOver = handleMouseOver;
        interactiveListeners.onMouseLeave = handleMouseLeave;
    }
    if (triggerList.focus) {
        childrenProps.onFocus = composeFocusEventHandler(handleFocus, childrenProps.onFocus);
        childrenProps.onBlur = composeFocusEventHandler(handleBlur, childrenProps.onBlur);
        interactiveListeners.onFocus = handleFocus;
        interactiveListeners.onBlur = handleBlur;
    }
    return (React.createElement(React.Fragment, null,
        React.cloneElement(children, childrenProps),
        React.createElement(TooltipWrapper, { ref: null, anchorEl: anchorEl, isOpen: isOpen, "$isString": typeof content === 'string', "$isWhite": isWhite, modifiers: modifiers, positions: positions, ...interactiveListeners, ...props },
            wrappedContent,
            withArrow ? React.createElement(Arrow, { ref: setArrowEl }) : null)));
});
